import { BackgroundJobRunStatus, BackgroundJobScheduleStatus } from 'noddi-async';
import { type BadgeVariant } from 'noddi-ui-web';
import { secondsToTimeString } from 'noddi-util';

const JOB_RUN_STATUS_VARIANT: Record<string, BadgeVariant> = {
  [BackgroundJobRunStatus.PENDING]: 'outline',
  [BackgroundJobRunStatus.STARTED]: 'info',
  [BackgroundJobRunStatus.SUCCESS]: 'success',
  [BackgroundJobRunStatus.FAILURE]: 'destructive',
  [BackgroundJobRunStatus.RETRY]: 'warning',
  [BackgroundJobRunStatus.REVOKED]: 'outline',
  [BackgroundJobRunStatus.STALLED]: 'warning',
  [BackgroundJobRunStatus.UNKNOWN]: 'warning'
};

const DEFERRED_STATUS_VARIANT: Record<string, BadgeVariant> = {
  [BackgroundJobScheduleStatus.PENDING]: 'info',
  [BackgroundJobScheduleStatus.DISPATCHING]: 'warning',
  [BackgroundJobScheduleStatus.DISPATCHED]: 'success',
  [BackgroundJobScheduleStatus.CANCELLED]: 'outline',
  [BackgroundJobScheduleStatus.FAILED]: 'destructive'
};

export const getJobRunStatusVariant = (status: string): BadgeVariant => JOB_RUN_STATUS_VARIANT[status] ?? 'outline';

export const getDeferredStatusVariant = (status: string): BadgeVariant => DEFERRED_STATUS_VARIANT[status] ?? 'outline';

export const shortDottedName = (name: string) => {
  if (!name) {
    return null;
  }
  const parts = name.split('.');
  return parts.length > 2 ? parts.slice(-2).join('.') : name;
};

const parseTimestampMs = (value: string): number => {
  const parsed = Date.parse(value);
  if (Number.isFinite(parsed)) {
    return parsed;
  }
  // JS Date rejects >3 fractional second digits on some engines.
  const trimmed = value.replace(/(\.\d{3})\d+/, '$1');
  return Date.parse(trimmed);
};

type RunDurationInput = {
  runtimeSeconds?: number | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  isActive?: boolean;
};

const formatDuration = (
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined,
  runtimeSeconds?: number | null,
  isActive?: boolean
) => {
  if (runtimeSeconds != null && Number.isFinite(runtimeSeconds) && runtimeSeconds >= 0 && !isActive) {
    return secondsToTimeString({
      seconds: Math.round(runtimeSeconds),
      shortFormat: true,
      includeDays: false
    });
  }
  if (!startedAt) {
    if (runtimeSeconds != null && Number.isFinite(runtimeSeconds) && runtimeSeconds >= 0) {
      return secondsToTimeString({
        seconds: Math.round(runtimeSeconds),
        shortFormat: true,
        includeDays: false
      });
    }
    return null;
  }
  const start = parseTimestampMs(startedAt);
  const end = finishedAt ? parseTimestampMs(finishedAt) : Date.now();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
    if (runtimeSeconds != null && Number.isFinite(runtimeSeconds) && runtimeSeconds >= 0) {
      return secondsToTimeString({
        seconds: Math.round(runtimeSeconds),
        shortFormat: true,
        includeDays: false
      });
    }
    return null;
  }
  const elapsed = Math.round((end - start) / 1000);
  const seconds =
    runtimeSeconds != null && Number.isFinite(runtimeSeconds) ? Math.max(elapsed, Math.round(runtimeSeconds)) : elapsed;
  return secondsToTimeString({ seconds, shortFormat: true, includeDays: false });
};

export const formatRunDuration = (run: RunDurationInput) =>
  formatDuration(run.startedAt, run.finishedAt, run.runtimeSeconds, run.isActive);

export const connectionLabel = (state: string) => {
  if (state === 'connected') {
    return 'Live';
  }
  if (state === 'reconnecting') {
    return 'Reconnecting…';
  }
  if (state === 'connecting') {
    return 'Connecting…';
  }
  if (state === 'error') {
    return 'Offline';
  }
  return state;
};
